import React, { FunctionComponent, useMemo } from 'react'
import { useIntl } from 'react-intl'

import styled from 'styled-components/native'

import { Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { NpTestPlanMeasureGroup } from './NpTestPlanMeasureGroup'
import { NpTestPlanMeasureSection } from './NpTestPlanMeasureSection'
import { groupMeasures } from '../data/npTestPlanMeasureUtils'
import {
  NpTestPlanInformant,
  NpTestPlanMeasure,
  NpTestPlanMeasureScope,
  NpTestPlanMeasureType,
} from '../data/npTestPlanTypes'
import { useNpTestPlanMeasureLabels } from '../hooks/useNpTestPlanMeasureLabels'

export interface NpTestPlanBuiltPanelProps {
  measures: NpTestPlanMeasure[]
  memberName: string
  onAddMeasures: (scope: NpTestPlanMeasureScope) => void
  onRemoveMeasure: (measure: NpTestPlanMeasure) => void
  isRemoveDisabled?: boolean
}

const Sections = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['24px'],
}))

export const NpTestPlanBuiltPanel: FunctionComponent<NpTestPlanBuiltPanelProps> = ({
  measures,
  memberName,
  onAddMeasures,
  onRemoveMeasure,
  isRemoveDisabled,
}) => {
  const { formatMessage } = useIntl()
  const { formatInformant, formatMeasureType } = useNpTestPlanMeasureLabels()

  const sections = useMemo(() => groupMeasures(measures, { includeEmpty: true }), [measures])

  const addLabels: Record<NpTestPlanMeasureType, string> = {
    [NpTestPlanMeasureType.RATING_SCALE]: formatMessage({
      defaultMessage: '+ Add scales',
      description: 'Link that opens the library of rating scales that can be added to a neuropsych test plan',
    }),
    [NpTestPlanMeasureType.F2F]: formatMessage({
      defaultMessage: '+ Add tests',
      description: 'Link that opens the library of face-to-face tests that can be added to a neuropsych test plan',
    }),
  }

  return (
    <Sections testID={tID('NpTestPlanBuiltPanel')}>
      {sections.map(({ measureType, groups }) => (
        <NpTestPlanMeasureSection
          key={measureType}
          testIdName={`NpTestPlanBuiltPanel-section-${measureType}`}
          title={formatMeasureType(measureType)}
        >
          {groups.map(({ informant, measures: grouped }) => {
            const isMember = informant === NpTestPlanInformant.SELF_REPORT
            return (
              <NpTestPlanMeasureGroup
                key={informant}
                testIdName={`NpTestPlanBuiltPanel-group-${measureType}-${informant}`}
                title={isMember && memberName ? memberName : formatInformant(informant)}
                subtitle={isMember && memberName ? formatInformant(informant) : undefined}
                measures={grouped}
                onRemoveMeasure={onRemoveMeasure}
                isRemoveDisabled={isRemoveDisabled}
                footer={
                  <Link
                    testID={tID(`NpTestPlanBuiltPanel-add-${measureType}-${informant}`)}
                    size={BodyTextSize.DEFAULT}
                    text={addLabels[measureType]}
                    onPress={() => onAddMeasures({ measureType, informant })}
                  />
                }
              />
            )
          })}
        </NpTestPlanMeasureSection>
      ))}
    </Sections>
  )
}
